import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/library/accordion";
import { Badge } from "@/components/ui/others/Badges";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import {
  CalendarClock,
  CalendarPlus,
  Handshake,
  Route,
  UserRound,
  UsersRound,
} from "lucide-react";
import { format, formatDistanceToNowStrict } from "date-fns";
import DatePickerCalendar from "@/features/leads/components/view-lead/components/Actions/components/DatePickerCalendar";
import { useFollowUpTimeEnabled } from "@/features/leads/api/useLeadFormConfig";
import type { LeadTrackFollowUp } from "@/features/stage/api/useLeadTrackFollowUps";
import type { EntityTrack } from "@/features/stage/types/stageTypes";
import { cn } from "@/lib/utils";

function getStatusTypeLabel(statusType?: string) {
  if (!statusType) return null;
  return statusType.charAt(0).toUpperCase() + statusType.slice(1);
}

/** ISO → Date, or null when the row carries something unparseable. */
function parseDate(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The picker's last step, worded for a track follow-up rather than a lead's. */
const TRACK_SCHEDULE_LABELS = {
  noteTitle: "Add a note",
  notePlaceholder: "Why is it moving? (optional)",
  noteHint:
    "Note is optional. The new date is saved either way, and the old one is kept in the track's history.",
  submit: "Reschedule follow-up",
} as const;

/**
 * Chip colour follows the CHOICE'S TYPE, not merely "is the lead on this
 * track" — a finished choice must not read the same as one still being chased
 * (user, 2026-09-23). The three types are the server's own `TrackStatusType`,
 * and `active` IS "Completed" (same naming the follow-up lifecycle's
 * CLOSED_CHOICE_TYPES uses), which is why it is the green one.
 *
 * NOTE `--colorblack-10` has no dash after "color" — `--color-black-10` is
 * undefined in `variables.css`, so the old text colour here silently resolved
 * to nothing and fell back to whatever was inherited.
 */
function getBadgeColor(isOnTrack: boolean, statusType?: string) {
  if (!isOnTrack)
    return "bg-[var(--color-choice)] text-[var(--colorblack-10)]";
  if (statusType === "active") return "bg-[var(--color-success)] text-white";
  if (statusType === "rejected") return "bg-[var(--color-high)] text-white";
  // "pending" — and any absent/unknown type, which keeps the old amber.
  return "bg-[var(--color-medium)] text-[var(--colorblack-10)]";
}

function getMarksText(statusType?: string) {
  if (statusType === "active") return "Mark as Completed";
  if (statusType === "rejected") return "Mark as Closed";
  if (statusType === "pending") return "Mark as Pending";
  return "Mark as Completed";
}

interface TrackItemProps {
  track: EntityTrack;
  /** Edit-or-higher on the role's track dial: may assign / change the choice. */
  canEdit?: boolean;
  /** Manage on the dial: may remove the track from the lead ("None"). */
  canRemove?: boolean;
  /** Manage: may hand the track from one member to another. */
  canReassign?: boolean;
  /** Opens the "who will handle this?" picker for an assigned track. */
  onChangeAssignee?: (trackId: number) => void;
  /** Live track follow-ups of THIS track (one per member role). */
  followUps?: LeadTrackFollowUp[];
  /** `date` is an ISO string; `note` is the optional line typed in the
   *  picker's last step (the server stores it on the new row). The server
   *  decides who may move it. */
  onRescheduleFollowUp?: (id: number, date: string, note?: string) => void;
  onAssignTrack: (trackId: number, subTrackId: number) => void;
  onSwitchSubTrack: (trackId: number, subTrackId: number) => void;
  onRemoveTrack: (trackId: number) => void;
}

/**
 * What this row edits, under the track's name: "Whole lead", or on a
 * deal-based track "This deal · <name>" (the deal chip the drawer is on).
 * Nothing on a track from an older server (no grain = whole lead, as always).
 */
function TrackScopeTag({ track }: { track: EntityTrack }) {
  if (!track.grain) return null;
  const perDeal = track.grain === "deal";
  const label = perDeal
    ? track.scope?.childName
      ? `This deal · ${track.scope.childName}`
      : "Per deal"
    : "Whole lead";
  const Icon = perDeal ? Handshake : UsersRound;
  return (
    <span
      className="mt-1 mr-2 inline-flex max-w-full items-center gap-1 rounded-full border border-[var(--color-black-1)] bg-white px-2 py-0.5 text-[11px] font-medium text-[#505e59] tracking-[-0.22px]"
      title={
        perDeal
          ? "This track is kept per deal: each deal has its own status, person and follow-up"
          : "This track is kept on the lead: one status, person and follow-up for the whole lead"
      }
    >
      <Icon size={12} strokeWidth={1.75} className="shrink-0" />
      <span className="truncate">{label}</span>
    </span>
  );
}

export default function TrackItem({
  track,
  canEdit: canEditProp = true,
  canRemove = true,
  canReassign = false,
  onChangeAssignee,
  followUps = [],
  onRescheduleFollowUp,
  onAssignTrack,
  onSwitchSubTrack,
  onRemoveTrack,
}: TrackItemProps) {
  // A deal-based track opened with no deal (the lead view): nothing here
  // edits it — each deal carries its own position.
  const needsDeal = Boolean(track.needsDeal);
  const canEdit = canEditProp && !needsDeal;
  // Tenant-wide "time on follow-ups" provision — the same one the lead's
  // follow-up picker reads, so a date-only workspace is date-only here too.
  const timeEnabled = useFollowUpTimeEnabled();
  const assignedAt = track.assignedAt ? parseDate(track.assignedAt) : null;
  const assignedAgo = assignedAt
    ? `${formatDistanceToNowStrict(assignedAt)} ago`
    : "";

  const activeSubTrack = track.subTracks.find((st) => st.isActive);
  const dealCount = (track.deals ?? []).length;
  const trackBadge = needsDeal
    ? dealCount > 0
      ? `On ${dealCount} deal${dealCount === 1 ? "" : "s"}`
      : "Needs a deal"
    : track.isActive
      ? (activeSubTrack?.name ?? "Active")
      : "Not Assigned";

  const badgeColor = getBadgeColor(
    track.isActive,
    activeSubTrack?.statusType
  );

  const isNoneActive = track.isActive === false;

  const selectValue = isNoneActive
    ? "none"
    : activeSubTrack
      ? String(activeSubTrack.id)
      : "none";

  const handleSubTrackClick = (subTrackId: number) => {
    if (track.isActive) {
      onSwitchSubTrack(track.id, subTrackId);
    } else {
      onAssignTrack(track.id, subTrackId);
    }
  };

  const handleSelectChange = (value: string) => {
    if (!canEdit) return;
    if (value === "none") {
      if (!isNoneActive && canRemove) onRemoveTrack(track.id);
      return;
    }
    const id = Number(value);
    const sub = track.subTracks?.find((s) => s.id === id);
    if (!sub || sub.isActive) return;
    handleSubTrackClick(id);
  };

  return (
    <AccordionItem
      value={track.id?.toString() || ""}
      className="border border-[var(--color-black-1)] rounded-[20px] mb-3 px-3 py-3 last:border-b relative overflow-hidden"
      style={{
        backgroundImage:
          "linear-gradient(177.477deg, rgb(237, 250, 245) 11.475%, rgb(255, 255, 255) 52.352%)",
      }}
    >
      <AccordionTrigger className="hover:no-underline py-0 !justify-start gap-2 items-center [&>svg]:translate-y-0 [&>svg]:shrink-0">
        <div className="flex gap-3 items-start flex-1 min-w-0">
          <div className="bg-[#eaf5f1] rounded-full p-2.5 shrink-0">
            <Route
              size={20}
              className="text-[var(--color-primary)]"
              strokeWidth={1.5}
            />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[16px] font-medium text-[#051912] tracking-[-0.32px] leading-normal">
              {track.name}
            </p>
            <TrackScopeTag track={track} />
            <p className="text-[12px] font-normal text-[#505e59] tracking-[-0.24px] leading-normal mt-1">
              {track.description ||
                "Manage the student's education loan steps independently."}
            </p>
            {/* Small, under the description: when this track was put on the
                lead. Only while the lead is ON the track. */}
            {track.isActive && assignedAt && (
              <p
                className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#7b8a84] tracking-[-0.22px]"
                title={format(assignedAt, "EEEE, d MMMM yyyy · h:mm a")}
              >
                <CalendarPlus size={12} strokeWidth={1.75} />
                Assigned {format(assignedAt, "d MMM yyyy")}
                <span className="text-[#a3aeaa]">· {assignedAgo}</span>
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2 items-center shrink-0">
          {/* Who handles it — visible without opening the row. */}
          {track.hasOwners && track.isActive && (
            <span className="inline-flex items-center gap-1 text-[12px] text-[#505e59]">
              <UserRound size={13} strokeWidth={1.75} />
              {(track.assignees ?? []).map((a) => a.name).join(", ") ||
                "Unassigned"}
            </span>
          )}
          <Badge
            className={`${badgeColor} text-[12px] font-medium px-2.5 py-0.5 rounded-full tracking-[-0.24px] h-[22px]`}
          >
            {trackBadge}
          </Badge>
        </div>
      </AccordionTrigger>

      <AccordionContent className="pt-4 pb-1">
        {needsDeal && (
          <div className="px-0.5 mb-4">
            {(track.deals ?? []).length === 0 ? (
              <p className="rounded-xl border border-dashed border-[var(--color-black-1)] bg-white/80 px-3 py-2.5 text-[12px] text-[#505e59]">
                Add a deal to use this track. It is kept per deal, so each
                deal gets its own status, person and follow-up.
              </p>
            ) : (
              <>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[#697571] mb-2">
                  On deals
                </label>
                <ul className="flex flex-col gap-1.5">
                  {(track.deals ?? []).map((d) => (
                    <li
                      key={d.childId}
                      className="flex items-center justify-between gap-2 rounded-xl border border-[var(--color-black-1)] bg-white px-3 py-2 text-[12px]"
                    >
                      <span className="truncate font-medium text-[#051912]">
                        {d.childName ?? `Deal #${d.childId}`}
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        {d.assignees.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-[#505e59]">
                            <UserRound size={12} strokeWidth={1.75} />
                            {d.assignees.map((a) => a.name).join(", ")}
                          </span>
                        )}
                        <Badge
                          className={`${getBadgeColor(true, d.subTrack?.statusType)} text-[11px] font-medium px-2 py-0.5 rounded-full h-[20px]`}
                        >
                          {d.subTrack?.name ?? "On track"}
                        </Badge>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-[#7b8a84]">
                  Open the deal to change it.
                </p>
              </>
            )}
          </div>
        )}

        {/* Track person — only on tracks some role holds at "Own". */}
        {track.hasOwners && track.isActive && (
          <div className="px-0.5 mb-4">
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[#697571] mb-2">
              Handled by
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {(track.assignees ?? []).map((a) => (
                <span
                  key={a.id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-black-1)] bg-white px-2.5 py-1 text-[12px] font-medium text-[#051912]"
                >
                  <UserRound size={13} strokeWidth={1.75} />
                  {a.name}
                  {a.roleName ? (
                    <span className="font-normal text-[#697571]">
                      · {a.roleName}
                    </span>
                  ) : null}
                </span>
              ))}
              {(track.assignees ?? []).length === 0 && (
                <span className="text-[12px] text-[#9ba3a0]">Unassigned</span>
              )}
              {onChangeAssignee && canReassign && (
                  <button
                    type="button"
                    onClick={() => onChangeAssignee(track.id)}
                    className="text-[12px] font-medium text-[var(--color-success)] hover:underline"
                  >
                    {(track.assignees ?? []).length === 0 ? "Assign" : "Change"}
                  </button>
                )}
            </div>
          </div>
        )}

        {followUps.length > 0 && (
          <div className="px-0.5 mb-4">
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[#697571] mb-2">
              Track follow-up
            </label>
            <div className="flex flex-col gap-2">
              {followUps.map((f) => {
                const due = parseDate(f.date);
                const canMove = Boolean(onRescheduleFollowUp);
                const dateLabel = due
                  ? format(due, "EEE, MMM d, yyyy")
                  : "No date";
                // Calendar-day reading, the same buckets the rail uses.
                const startOfToday = new Date();
                startOfToday.setHours(0, 0, 0, 0);
                const dayDiff = due
                  ? Math.round(
                      (new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime() -
                        startOfToday.getTime()) /
                        86_400_000,
                    )
                  : null;
                const overdue = dayDiff != null && dayDiff < 0;
                const whenLabel =
                  dayDiff == null
                    ? ""
                    : dayDiff === 0
                      ? "Due today"
                      : dayDiff === 1
                        ? "Due tomorrow"
                        : dayDiff > 1
                          ? `In ${dayDiff} days`
                          : dayDiff === -1
                            ? "Overdue since yesterday"
                            : `Overdue by ${-dayDiff} days`;
                // ONE-TAP re-dates: nine times out of ten a follow-up is just
                // pushed a day or a week. Same hour as the current one when the
                // tenant keeps times, start of day otherwise.
                const quick = (days: number) => {
                  const d = new Date();
                  d.setDate(d.getDate() + days);
                  if (timeEnabled && due) d.setHours(due.getHours(), due.getMinutes(), 0, 0);
                  else d.setHours(0, 0, 0, 0);
                  onRescheduleFollowUp?.(f.id, d.toISOString(), f.note?.trim() || undefined);
                };
                const timeLabel = !due
                  ? ""
                  : timeEnabled
                    ? format(due, "h:mm a")
                    : "All day";

                // The trigger is the row's right-hand chip: the same calendar
                // tile + date-over-time stack the lead's follow-up uses, so
                // both pickers read as one control.
                const trigger = (
                  <button
                    type="button"
                    disabled={!canMove}
                    title={
                      canMove
                        ? "Reschedule this track follow-up"
                        : "You can't move this follow-up"
                    }
                    className={cn(
                      "group/tfu inline-flex shrink-0 items-center gap-2 rounded-xl border-[1.5px] py-1 pl-1.5 pr-2.5 transition-all",
                      !canMove
                        ? "cursor-not-allowed border-[var(--color-black-2)] bg-white opacity-70"
                        : overdue
                          ? "cursor-pointer border-[var(--color-error)]/35 bg-[var(--color-error)]/[0.05] hover:border-[var(--color-error)]/60"
                          : "cursor-pointer border-[var(--color-success)]/30 bg-[var(--color-success)]/[0.04] hover:border-[var(--color-success)]/55",
                    )}
                  >
                    <span
                      className={cn(
                        "inline-flex size-7 shrink-0 items-center justify-center rounded-lg transition-colors",
                        !canMove
                          ? "bg-[var(--color-black-1)] text-[var(--color-black-7)]"
                          : overdue
                            ? "bg-[var(--color-error)] text-white"
                            : "bg-[var(--color-success)] text-white",
                      )}
                    >
                      <CalendarClock size={14} strokeWidth={2} />
                    </span>
                    <span className="flex min-w-0 flex-col items-start leading-tight">
                      <span className="text-[12px] font-semibold tabular-nums text-[var(--color-text-primary)]">
                        {dateLabel}
                      </span>
                      <span className="text-[10.5px] tabular-nums text-[var(--color-text-gray)]">
                        {timeLabel}
                      </span>
                    </span>
                  </button>
                );

                return (
                  <div
                    key={f.id}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-xl border border-[var(--color-black-1)] bg-white px-3 py-2.5"
                  >
                    <span className="flex min-w-0 flex-col gap-0.5 text-[12px] text-[#051912]">
                      <span className="flex items-center gap-2 min-w-0">
                        <UserRound
                          size={14}
                          strokeWidth={1.75}
                          className="shrink-0 text-[var(--color-success)]"
                        />
                        <span className="truncate">
                          {f.holder?.name ?? "Unassigned"}
                          {f.role?.name ? (
                            <span className="text-[#697571]">
                              {" "}
                              · {f.role.name}
                            </span>
                          ) : null}
                        </span>
                      </span>
                      {whenLabel ? (
                        <span
                          className={cn(
                            "pl-[22px] text-[11px] font-medium",
                            overdue ? "text-[var(--color-error)]" : "text-[#697571]",
                          )}
                        >
                          {whenLabel}
                        </span>
                      ) : null}
                      {f.note?.trim() ? (
                        <span className="line-clamp-2 pl-[22px] text-[11px] text-[#697571]" title={f.note}>
                          “{f.note}”
                        </span>
                      ) : null}
                    </span>

                    <span className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    {canMove &&
                      ([
                        ["Tomorrow", 1],
                        ["+3 days", 3],
                        ["Next week", 7],
                      ] as const).map(([label, days]) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => quick(days)}
                          title={`Move this follow-up to ${label.toLowerCase()}`}
                          className="rounded-full border border-[var(--color-black-2)] bg-white px-2.5 py-1 text-[11px] font-medium text-[#505e59] transition-colors hover:border-[var(--color-success)]/50 hover:bg-[var(--color-success)]/[0.05] hover:text-[var(--color-success)] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-success)]"
                        >
                          {label}
                        </button>
                      ))}
                    {canMove ? (
                      <DatePickerCalendar
                        // Opens BELOW the chip, right edges aligned — the way
                        // the notes popover sits under its trigger (user
                        // directive 2026-09-20). Left to itself it chose "top"
                        // and covered "Assigned to" and the drawer header.
                        popoverSide="bottom"
                        popoverAlign="end"
                        // A follow-up is re-dated, never cleared — same rule
                        // as the lead's (2026-08-20).
                        allowClear={false}
                        // The tenant's own "capture a time" provision: track
                        // follow-ups are follow-ups, so they follow it too.
                        enableTime={timeEnabled}
                        value={due ?? undefined}
                        scheduleLabels={TRACK_SCHEDULE_LABELS}
                        onCreateFollowUp={({ date, note }) =>
                          onRescheduleFollowUp?.(
                            f.id,
                            date.toISOString(),
                            note?.trim() || undefined,
                          )
                        }
                      >
                        {trigger}
                      </DatePickerCalendar>
                    ) : (
                      trigger
                    )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className={cn("px-0.5", needsDeal && "hidden")}>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[#697571] mb-2">
            Track status
            {!canEdit && (
              <span className="ml-2 normal-case tracking-normal font-normal text-[#9ba3a0]">
                · view only
              </span>
            )}
          </label>
          <Select
            value={selectValue}
            onValueChange={handleSelectChange}
            disabled={!canEdit}
          >
            <SelectTrigger
              size="default"
              className={cn(
                "w-full h-11 min-h-11 rounded-xl border-[var(--color-black-1)] bg-white/90 shadow-none",
                "px-3.5 text-left font-medium text-[#051912] hover:bg-white",
                "data-[size=default]:h-11",
                !canEdit && "cursor-not-allowed opacity-70"
              )}
            >
              <SelectValue placeholder="Choose a status…" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              className="rounded-xl border-[var(--color-black-1)] z-[100] min-w-[var(--radix-select-trigger-width)]"
            >
              {/* "None" removes the track — Manage only. An Edit-level role
                  on an assigned track simply doesn't get the option. */}
              {(canRemove || isNoneActive) && (
                <SelectItem
                  value="none"
                  className="rounded-lg py-2.5 cursor-pointer"
                >
                  None · Unassigned
                </SelectItem>
              )}

              {(canRemove || isNoneActive) &&
              track.subTracks &&
              track.subTracks.length > 0 ? (
                <SelectSeparator className="my-1" />
              ) : null}

              {track.subTracks?.map((subTrack) => {
                const statusLabel = getStatusTypeLabel(subTrack.statusType);
                const detailBits = [
                  getMarksText(subTrack.statusType),
                  statusLabel,
                ].filter(Boolean) as string[];
                const optionLabel =
                  detailBits.length > 0
                    ? `${subTrack.name} · ${detailBits.join(" · ")}`
                    : subTrack.name;
                return (
                  <SelectItem
                    key={subTrack.id}
                    value={String(subTrack.id)}
                    textValue={optionLabel}
                    className="rounded-lg py-2.5 cursor-pointer text-left leading-snug min-h-[3rem] items-start"
                  >
                    <span className="flex flex-col gap-0.5 items-start text-left w-full py-0.5">
                      <span className="text-sm font-semibold text-[#051912]">
                        {subTrack.name}
                      </span>
                      {detailBits.length > 0 ? (
                        <span className="text-[11px] font-medium text-[#697571]">
                          {detailBits.join(" · ")}
                        </span>
                      ) : null}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
